<template>
  <div class="row">
    <div class="col-sm-12 col-md-6 p-2">
      <v-calendar
        class="compact-calendar"
        ref="events-calendar"
        color="red"
        :min-date="minimumDate"
        :attributes="calendarOptions"
        @update:from-page="calendarPageNavigated"
        @dayclick="calendarDateClicked"
        :locale="getNavigatorLanguage"
      >
        <template v-slot:header-left-button>
          <div class="calendar-navigation-btn">
            <i class="far fa-long-arrow-left"></i>
          </div>
        </template>
        <template v-slot:header-right-button>
          <div class="calendar-navigation-btn">
            <i class="far fa-long-arrow-right"></i>
          </div>
        </template>
      </v-calendar>
    </div>
    <div class="col-sm-12 col-md-6 p-2">
      <div
        class="d-flex flex-column align-items-center justify-content-between h-100"
      >
        <div class="w-100">
          <div
            class="mb-2 d-flex flex-row justify-content-between align-items-center"
          >
            <span class="c-charcoal">{{
              $formatDate(
                selectedDate,
                'date-word-long',
                $t('PRODUCTS.LABELS.SELECT_A_DATE')
              )
            }}</span>
            <span>
              <i class="fa fa-clock-o mr-1"></i>
              {{ meetingType.duration_minutes
              }}{{ $t('GENERAL.LABEL.MINUTES_ABBREVIATION') }}
            </span>
          </div>
          <div class="mb-3">
            <v-select
              :options="timezones"
              label="name"
              code="value"
              v-model="selectedTimezone"
              :reduce="r => r.value"
              @input="loadData"
            >
            </v-select>
          </div>
          <div
            class="d-flex flex-row flex-wrap justify-content-center justify-content-md-between"
          >
            <div
              class="day-slot rounded bg-brand-color c-white text-center px-3 py-1 mb-2 mx-2 mx-md-0 pointer"
              :class="{ selected: selectedSlot === slot }"
              v-for="(slot, index) in selectedDailySlots"
              :key="index"
              @click="() => (selectedSlot = slot)"
            >
              {{ slot.format('hh:mm A') }}
            </div>
          </div>
        </div>
        <div class="w-100 mt-4">
          <HMButton
            v-if="selectedSlot"
            class="w-100 text-center"
            color="brand"
            size="md"
            @click="$emit('selectedSlot', { selectedSlot, selectedTimezone })"
            :disabled="isBooking"
            :loading="isBooking"
          >
            <span class="text-center w-100">
              {{
                $t('PRODUCTS.BUTTON.BOOK_SLOT', {
                  slot: $formatDate(selectedSlot, 'date-time'),
                })
              }}
            </span>
          </HMButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import moment from 'moment-timezone';
import colors from '@/core/styles/colors.scss';
import { getNavigatorLanguage } from '@/web/utils/languages';
import timezones from '@/web/utils/timezones.ts';
import _ from 'lodash';
import { errorToMessage } from '../../../core/utils/errors';

export default Vue.extend({
  name: 'PublicSchedulingCalendar',
  components: {},
  props: {
    studioURL: {
      type: String,
    },
    meetingType: {
      type: Object,
    },
    preselectedDate: {
      type: Date,
      default: () => new Date(),
    },
    isBooking: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      studio: 'studio/currentStudio',
    }),
    timezones() {
      return timezones;
    },
    calendarOptions() {
      return [...this.calendarOptionsConfig, this.dottedDays];
    },
    selectedDailySlots() {
      if (this.isLoading) return [];
      const selectedDateSlots = this.timeSlots.filter(slot =>
        slot.isSame(this.selectedDate, 'day')
      );
      return selectedDateSlots;
    },
    getNavigatorLanguage,
    dottedDays() {
      return {
        dot: {
          style: {
            backgroundColor: this.studio?.primary_color || colors.coral,
          },
        },
        dates: this.dottedDates,
      };
    },
  },
  data() {
    return {
      selectedCalendarPage: {
        month: this.preselectedDate.getMonth(),
        year: this.preselectedDate.getFullYear(),
      },
      selectedDate: this.preselectedDate,
      minimumDate: new Date(),
      selectedSlot: null,
      selectedTimezone: null,
      dottedDates: [],
      calendarOptionsConfig: [
        {
          key: 'today',
          highlight: {
            class: 'todays-date-background',
            contentClass: 'todays-date-content',
          },
          dates: new Date(),
        },
      ],
      daySelected: false,
      isLoading: true,
      timeSlots: null,
    };
  },
  methods: {
    async calendarPageNavigated(page) {
      this.selectedCalendarPage = page;
      if (!this.selectedTimezone) return;
      this.selectedSlot = null;
      await this.loadData();
      this.daySelected = true;
      this.$refs['events-calendar'].focusDate(this.selectedDate);
    },
    calendarDateClicked(day) {
      this.selectedSlot = null;
      this.selectedDate = moment(day.date).subtract(12, 'hours').toDate();
      this.daySelected = true;
    },
    async loadData(initial = false) {
      this.isLoading = true;
      const y = this.selectedCalendarPage.year;
      const m = this.selectedCalendarPage.month - 1;
      const firstDay = moment(new Date(y, m, 1)).tz(this.selectedTimezone);
      const lastDay = moment(new Date(y, m + 1, 0))
        .add('days', 1)
        .tz(this.selectedTimezone);
      try {
        let timeSlots = await this.$store.dispatch(
          'marketing/getPublicMeetingTypeTimeSlots',
          {
            studioURL: this.studioURL,
            slug: this.meetingType.slug,
            startDate: firstDay,
            endDate: lastDay,
            timezone: this.selectedTimezone,
          }
        );
        if (initial && !timeSlots.length) {
          // if nothing is found in the first month, try the next 2 months
          const extendedLastDay = moment(new Date(y, m + 3, 0))
            .add('days', 1)
            .tz(this.selectedTimezone);

          timeSlots = await this.$store.dispatch(
            'marketing/getPublicMeetingTypeTimeSlots',
            {
              studioURL: this.studioURL,
              slug: this.meetingType.slug,
              startDate: lastDay,
              endDate: extendedLastDay,
              timezone: this.selectedTimezone,
            }
          );
        }
        // map timeslots to moment objects and remove any past slots
        const now = moment().tz(this.selectedTimezone);
        this.timeSlots = timeSlots
          .map(slot => moment(slot).tz(this.selectedTimezone))
          .filter(slot => slot.isSameOrAfter(now));

        this.dottedDates = [];
        this.timeSlots.forEach(slot => {
          if (slot.isSameOrAfter(now, 'day')) {
            this.dottedDates.push(slot.toDate());
          }
          if (!this.minimumDate || slot.isBefore(this.minimumDate)) {
            this.minimumDate = slot.toDate();
          }
        });
        if (this.dottedDates.length) {
          this.selectedDate = this.dottedDates[0];
          this.$refs['events-calendar'].focusDate(this.selectedDate);
        } else {
          this.selectedDate = this.preselectedDate;
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load available time slots',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.loadData(true);
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.compact-calendar {
  font-family: var(--brand-font, 'Poppins');
}

::v-deep .todays-date-background {
  background-color: $charcoal;
  border-radius: 50%;
  box-sizing: content-box;
}

::v-deep .todays-date-content {
  color: $white;
}

::v-deep .vc-day-content {
  padding: 1rem;
}

/* move dot a bit lower*/
::v-deep .selectedDailyEvents {
  height: 38px;
}

::v-deep .vc-dots {
  height: 0;
}

/* move buttons away from borders */
::v-deep .vc-arrows-container {
  padding-top: 12px;
  padding-left: 36px;
  padding-right: 36px;

  @media screen and (max-width: 992px) {
    padding-left: 20px;
    padding-right: 20px;
  }
}

::v-deep .vc-arrow {
  &:hover {
    background-color: white;
  }
}

::v-deep .vc-day-box-center-bottom {
  height: 40px;
}

::v-deep .vc-container {
  color: $dark-gray;
  width: 100%;
  max-width: 100%;
  border: none;
}

::v-deep .vc-day {
  width: unset;
}

::v-deep .vc-day-content {
  &:hover {
    background-color: $dark-gray !important;
    color: $white !important;
  }
  &:focus {
    background-color: var(--brand-color, $coral) !important;
    color: $white !important;
  }
}

::v-deep .vc-header {
  margin-bottom: 1rem;
}

::v-deep .vc-grid-cell {
  padding: 0.5rem;
}

::v-deep .vc-title {
  color: $dark-gray !important;
  border-radius: 6px;
  border: 1px solid $medium-gray;
  padding: 6px 10px !important;

  &:before {
    color: var(--brand-color, $coral);
    content: '';
    font-family: 'Font Awesome 5 Pro';
    font-weight: 400;
    display: inline-block;
    margin-right: 5px;
  }
}

::v-deep .vc-nav-popover-container {
  background-color: $charcoal;
}

.calendar-navigation-btn {
  margin-top: 8px;
  border-radius: 6px;
  border: 1px solid $medium-gray;
  padding: 6px 10px;

  &:hover {
    color: var(--brand-color, $coral);
    border-color: var(--brand-color, $coral);
    box-shadow: 0px 0px 1px 1px var(--brand-color, $coral);
    background-color: $white;
  }
}

.day-slot {
  min-width: 120px;
  animation: fliparound 0.5s ease-out;
  border: 1px solid var(--brand-color, $coral) !important;
  border-radius: $border-radius;
  background-color: white !important;
  color: var(--brand-color, $coral) !important;

  &.selected {
    color: white !important;
    background-color: var(--brand-color, $coral) !important;
  }

  &:hover {
    color: white !important;
    background-color: var(--brand-color, $coral) !important;
  }
}

@keyframes fliparound {
  0% {
    transform: rotateX(-120deg);
  }
  100% {
    transform: rotateX(0deg);
  }
}
</style>
